import { useScanToPrint } from '~/modules/print/hooks/useScanToPrint';
import { PrintAssetType } from '~/modules/print/types/print.type';
import {
  FullStoryEvent,
  fullStoryTracking,
  type FullStoryEventProperties,
} from '~/utils/fullstory/tracking';

import { type RawScanEvent, type ScanEvent } from '../types/scanEvent.type';
import {
  type ScanEventTracker,
  type ScanSuppressionReason,
} from '../types/scanEventTelemetry.type';
import { extractScanErrorInfo } from '../utils/scanEventTelemetry.util';
import { useScanDeviceEvents } from './useScanDeviceEvents';

export type UseScanTelemetryReturn = {
  trackSuppressed: (rawScanEvent: RawScanEvent, reason: ScanSuppressionReason) => void;
} & Required<ScanEventTracker>;

const isReportable = (scanEvent: ScanEvent) => scanEvent.scanContext.isConfiguredForScanToPrint;

/**
 * FullStory reporting is best-effort, as in the print lifecycle instrumentation
 * (QPACK-299). These calls run inside the scan action handler, which treats any
 * exception as an action failure, so a FullStory error must never escape: it would
 * skip the action or report a successful scan as failed.
 */
const reportToFullStory = <E extends keyof FullStoryEventProperties>(
  name: E,
  properties: FullStoryEventProperties[E],
): void => {
  try {
    fullStoryTracking(name, properties);
  } catch {
    // FullStory forwarding is best-effort; swallow so scan handling is unaffected.
  }
};

/**
 * Tells the two causes of `SCAN_NO_ACTION_MATCHED` apart: a barcode that did not
 * resolve (unknown, or its lookup failed) versus one that resolved but matched no
 * action. Every scan action requires barcode content, so a dispatch always has one.
 */
const isBarcodeResolved = (scanEvent: ScanEvent) => Boolean(scanEvent.scanContext.barcodeContent);

/**
 * The single telemetry entry point for the scan pipeline: reports each action handler
 * outcome as `device.scan.*` events and as FullStory events, and each scan the queue
 * rejects as a FullStory event (QPACK-658).
 *
 * - action resolved, about to execute → `Scan Action Dispatched`
 * - `device.scan.completed`           → `Scan Action Result` (`result: 'success'`)
 * - `device.scan.failed`              → `Scan Action Result` (`result: 'failure'`)
 * - queue rejected the scan           → `Scan Suppressed`
 *
 * Only scans captured while the workstation was configured for scan-to-print are
 * reported. Every scan telemetry gate lives here, so all streams report the same
 * population. Handler outcomes read the configuration off the scan context snapshot
 * taken when the scan was enriched; a rejected scan never gets a context, so its gate
 * reads the current configuration instead.
 *
 * The barcode value is deliberately left out of FullStory: it is high-cardinality and
 * already captured on the device event.
 */
export const useScanTelemetry = (): UseScanTelemetryReturn => {
  const scanDeviceEvents = useScanDeviceEvents();
  const { isConfiguredForScanToPrint } = useScanToPrint(PrintAssetType.ShippingLabel);

  return {
    trackCompleted: (scanEvent, actionName) => {
      if (!isReportable(scanEvent)) return;

      scanDeviceEvents.trackCompleted(scanEvent, actionName);
      reportToFullStory(FullStoryEvent.SCAN_ACTION_RESULT, {
        barcode_resolved: isBarcodeResolved(scanEvent),
        correlation_id: scanEvent.correlationId,
        result: 'success',
        scan_action: actionName,
      });
    },
    trackDispatched: (scanEvent, actionName) => {
      if (!isReportable(scanEvent)) return;

      reportToFullStory(FullStoryEvent.SCAN_ACTION_DISPATCHED, {
        correlation_id: scanEvent.correlationId,
        scan_action: actionName,
      });
    },
    trackFailed: (scanEvent, error, actionName) => {
      if (!isReportable(scanEvent)) return;

      scanDeviceEvents.trackFailed(scanEvent, error, actionName);
      reportToFullStory(FullStoryEvent.SCAN_ACTION_RESULT, {
        barcode_resolved: isBarcodeResolved(scanEvent),
        correlation_id: scanEvent.correlationId,
        error_code: extractScanErrorInfo(error).errorCode,
        result: 'failure',
        scan_action: actionName,
      });
    },
    trackSuppressed: ({ correlationId }, reason) => {
      if (!isConfiguredForScanToPrint) return;

      reportToFullStory(FullStoryEvent.SCAN_SUPPRESSED, { correlation_id: correlationId, reason });
    },
  };
};
